import { useCallback, useDeferredValue, useState } from 'react';
import { useDebounce } from '@gonfalon/async/useDebounce';

/**
 * Manages both "current" and "deferred" state values using React's `useDeferredValue`.
 * The "current" value updates immediately for responsive UI, while the "deferred" value
 * is debounced and can trigger Suspense boundaries without blocking the immediate update.
 *
 * Use case: Text inputs that need instant feedback while triggering Suspense-inducing operations
 * like query refetches, such as when updating a search field and refetching a list of results.
 */
export function useDeferredState<T>(initialState: T, delay = 250) {
  const [currentState, setCurrentState] = useState(initialState);
  const [stateToDefer, setStateToDefer] = useState(currentState);
  const debouncedSetStateToDefer = useDebounce(setStateToDefer, delay);
  const deferredState = useDeferredValue(stateToDefer);

  const setDefferedState = useCallback(
    (newValue: T) => {
      setCurrentState(newValue);
      debouncedSetStateToDefer(newValue);
    },
    [debouncedSetStateToDefer],
  );

  const isPending = deferredState !== stateToDefer;

  return [{ current: currentState, deferred: deferredState }, setDefferedState, isPending] as const;
}
